import { name, MotionEffectsInitCallbackSymbol } from '../symbols'
import { named, optional, withDependencies } from '@wix/thunderbolt-ioc'
import {
	ICompsLifeCycle,
	CompsLifeCycleSym,
	DSViewModeAPISymbol,
	EditorFeatureConfigSymbol,
	FeatureStateSymbol,
	PageFeatureConfigSymbol,
	pageIdSym,
	IViewMode,
} from '@wix/thunderbolt-symbols'

import type {
	GetMotionEffectsInitCallback,
	MotionEffectsEditorConfig,
	MotionEffectsFeatureState,
	MotionEffectsPageConfig,
} from '../types'
import { IFeatureState } from 'thunderbolt-feature-state'
import { EditorNavigationSymbol, IEditorNavigation } from 'ds-feature-editor-navigation'
import { MotionEffectsManager } from '../MotionEffectsManager'
import { Animations, IAnimations } from 'feature-animations'
import { IEditorMotionEffectsInit } from '../types'
import viewport from '../viewport'

const MOTION_EFFECTS_DS_CALLBACK = 'MotionEffectsDSCallback'

const editorMotionEffectsFactory = (
	featureConfig: MotionEffectsPageConfig,
	editorConfig: MotionEffectsEditorConfig,
	featureState: IFeatureState<MotionEffectsFeatureState>,
	pageId: string,
	navigation: IEditorNavigation,
	getMotionEffectsInitCallback: GetMotionEffectsInitCallback,
	compsLifeCycle: ICompsLifeCycle,
	viewModeApi: IViewMode,
	animationsProvider?: IAnimations
): IEditorMotionEffectsInit => {
	let shouldInitMotionEffects = editorConfig.shouldEnableMotionEffects
	let unregisterFromCompLifeCycle = () => {}
	const compIds = Object.keys(featureConfig.animationDefsByCompId || {})
	function destroy() {
		const state = featureState.get()

		if (state) {
			state[pageId]?.clearAnimations()
			state[pageId]?.clearState()
		}
	}

	const motionEffectsManagerInitPromise: Promise<MotionEffectsManager | undefined> = new Promise((resolve) => {
		const { animationDefsByCompId, isResponsive } = featureConfig

		if (!animationsProvider) {
			resolve(undefined)
		}

		animationsProvider!.getEffectsInstance().then((animationsManager) => {
			const motionEffectsManager =
				featureState.get()?.[pageId] ?? new MotionEffectsManager(animationsManager, isResponsive)

			const allCompsEffectsMap = Object.assign({}, ...Object.values(animationDefsByCompId || {}))

			motionEffectsManager.motionViewport =
				motionEffectsManager.motionViewport ?? viewport({ manager: motionEffectsManager })

			motionEffectsManager.init(allCompsEffectsMap)
			featureState.update((state) => ({ ...state, [pageId]: motionEffectsManager }))

			resolve(motionEffectsManager)
		})
	})

	async function toggleMotionEffects() {
		if (shouldInitMotionEffects) {
			const isDuringViewModeSwitch = viewModeApi.isDuringViewModeSwitch()
			const motionEffectsManager = await motionEffectsManagerInitPromise
			const initCallback = getMotionEffectsInitCallback(motionEffectsManager)

			// tap into lifecycle (mount) hook to get and store the displayedId - support for Repeaters
			unregisterFromCompLifeCycle = compsLifeCycle.registerToCompLifeCycle(
				compIds,
				MOTION_EFFECTS_DS_CALLBACK,
				(compId: string, displayedId: string, element: HTMLElement) => {
					if (isDuringViewModeSwitch) {
						destroy()
					}

					if (!initCallback) {
						return
					}

					navigation.getNextNavigationIntent().then(() => {
						initCallback(compId, displayedId, element)
					})
				}
			)
		} else {
			destroy()
		}
	}

	return {
		name: 'editorMotionEffectsFactory',
		async pageWillMount() {
			// master page components with screen in are triggered as part of edit to preview mode navigation
			shouldInitMotionEffects = editorConfig.shouldEnableMotionEffects && pageId !== 'masterPage'
			await toggleMotionEffects()
		},
		async pageWillUnmount() {
			if (compIds.length && shouldInitMotionEffects) {
				unregisterFromCompLifeCycle()
				const motionEffectsManager = await motionEffectsManagerInitPromise
				motionEffectsManager?.motionViewport?.disconnect()
			}
		},
		getInstance() {
			return motionEffectsManagerInitPromise
		},
	}
}

export const MotionEffects = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(EditorFeatureConfigSymbol, name),
		named(FeatureStateSymbol, name),
		pageIdSym,
		EditorNavigationSymbol,
		MotionEffectsInitCallbackSymbol,
		CompsLifeCycleSym,
		DSViewModeAPISymbol,
		optional(Animations),
	],
	editorMotionEffectsFactory
)
